import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AiConversationFeatureEnum,
  AiConversationFragmentFragment,
  AiConversationStatusEnum,
  AiMessageStatusEnum,
  CompanyPlanUpgradeFeatureEnum,
  User,
} from 'graphql-common';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import UpgradePlanModal from '@lib/components/UpgradePlanModal/UpgradePlanModal';
import { GetUserAccessByCompany } from '@lib/components/UpgradePlanModal/utils/upgradePlanFeatureOptions';
import { PlanState } from '@lib/enums/plan';
import toast from '@lib/components/Toast/Toast';
import { logger } from '@lib/services/logger';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import ChatSidebar from './ChatSidebar';
import { AiChatDocRef } from './AiChatInsertContext';
import ConversationPane from './ConversationPane';
import AiRenameChatModal from './AiRenameChatModal';
import useAiBudgetStatus from './hooks/useAiBudgetStatus';
import useAiConversations from './hooks/useAiConversations';
import { AiAssistantTexts } from './getAiAssistantTexts';
import styles from './AiAssistant.module.scss';

// How long to wait for the streamed TITLE frame after a successful turn
// before giving up and settling the sidebar item on its placeholder title.
// Generous relative to typical title-generation latency (well under a second
// in practice) so it only fires on a genuinely lost/stalled frame.
const TITLE_WAIT_TIMEOUT_MS = 20000;

export type AiAssistantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  // The AI feature (assistant) conversations belong to.
  feature: AiConversationFeatureEnum;
  // Entity-dependent copy (see getAiAssistantTexts).
  texts: AiAssistantTexts;
  // Feeds the plan modal below, which the assistant owns its own instance of:
  // the layout's shared one is published through the router's outlet context,
  // and the assistant is mounted outside the Outlet (see
  // AiAssistantLauncherProvider).
  getUserAccessByCompany?: GetUserAccessByCompany;
  // Documents pre-attached to the composer of the new chat the modal opens on
  // (the document view's "Ask"). Read on each open; dropped once the user
  // switches chats or the chat is deleted.
  initialDocs?: AiChatDocRef[];
};

function AiAssistantModal(props: AiAssistantModalProps) {
  const {
    isOpen,
    onClose,
    user,
    feature,
    texts,
    getUserAccessByCompany,
    initialDocs,
  } = props;
  const { t } = useTranslation();
  // Opened from an out-of-credits error banner.
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  // null = new (not yet created) chat
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  // Conversation returned by aiSendMessage for a new chat — the sidebar list
  // may not include it until the next refetch completes.
  const [createdConversation, setCreatedConversation] =
    useState<AiConversationFragmentFragment | null>(null);
  // Pane remount key. Bumped only on an explicit switch (sidebar select / new
  // chat / destroy) so turn state resets then — but NOT when sending the first
  // message auto-creates a conversation, so the optimistic user message and the
  // in-flight stream survive the null -> created-id transition.
  const [paneKey, setPaneKey] = useState(0);
  // Id of the chat whose rename modal is open (null = closed).
  const [renameTargetId, setRenameTargetId] = useState<string | null>(null);
  // A chat whose first turn ended in anything but success, so the BE never
  // enqueued title generation for it (that runs on the success path only) and
  // no title is coming. Without this the sidebar item spins on its title loader
  // forever — see ChatHistoryItem's `titleUnavailable`.
  const [untitledConversationId, setUntitledConversationId] = useState<
    string | null
  >(null);
  // Give-up timer for a SUCCESSFUL turn's title: it streams as a separate TITLE
  // frame (no delivery guarantee, no ActionCable replay) after the terminal
  // frame, so it can arrive late or never. Without a bound the sidebar item
  // would spin forever if it's lost — mirrors the failed-turn placeholder
  // fallback below, just on a delay instead of immediately.
  const titleWaitTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(titleWaitTimerRef.current), []);
  // `initialDocs` of the current open, until the user leaves the new chat it
  // was meant for. Seeded on every close→open transition (below); the useState
  // initialiser only matters if the modal mounts already open.
  const [pendingDocs, setPendingDocs] = useState(initialDocs);

  // The modal instance stays mounted across close/open (only isOpen toggles),
  // so reset to a fresh new chat each time it is reopened instead of restoring
  // the last selected conversation.
  const prevIsOpenRef = useRef(isOpen);
  if (prevIsOpenRef.current !== isOpen) {
    prevIsOpenRef.current = isOpen;
    if (isOpen) {
      setSelectedConversationId(null);
      setCreatedConversation(null);
      setRenameTargetId(null);
      setUntitledConversationId(null);
      clearTimeout(titleWaitTimerRef.current);
      setPendingDocs(initialDocs);
      setPaneKey((key) => key + 1);
    }
  }

  const {
    conversations,
    search,
    setSearch,
    refetch,
    renameConversation,
    renameLoading,
    renameError,
    destroyConversation,
    destroyingId,
  } = useAiConversations({
    skip: !isOpen,
    onDestroyed: (id) => {
      if (id === selectedConversationId) {
        setSelectedConversationId(null);
        setPendingDocs(undefined);
        setPaneKey((key) => key + 1);
      }
    },
  });

  useEffect(() => {
    logger.debug(
      isOpen ? 'AiAssistant: modal opened' : 'AiAssistant: modal closed',
      { feature },
    );
  }, [isOpen, feature]);

  // Re-sync the credits meter whenever the user arrives at a chat: opening the
  // modal, or switching to another conversation. The live subscription normally
  // keeps it current on its own, so this is a floor rather than the mechanism —
  // it covers the modal being opened after a spell with a dropped cable, where
  // the first thing the user sees would otherwise be a stale number.
  const { refetch: refetchAiBudget } = useAiBudgetStatus(isOpen);
  useEffect(() => {
    if (isOpen) refetchAiBudget();
  }, [isOpen, selectedConversationId, refetchAiBudget]);

  const selectedConversation =
    conversations.find(({ id }) => id === selectedConversationId) ||
    (createdConversation?.id === selectedConversationId
      ? createdConversation
      : null);

  const handleSelectConversation = (id: string | null) => {
    logger.debug('AiAssistant: conversation selected', {
      conversationId: id,
      isNewChat: id === null,
    });
    setSelectedConversationId(id);
    setPendingDocs(undefined);
    // Explicit switch: force a fresh pane so the previous turn state is dropped.
    setPaneKey((key) => key + 1);
  };

  const handleConversationCreated = (
    conversation: AiConversationFragmentFragment,
  ) => {
    logger.debug('AiAssistant: switching to created conversation', {
      conversationId: conversation.id,
    });
    setCreatedConversation(conversation);
    setSelectedConversationId(conversation.id);
    refetch();
  };

  // A successful turn is the only one the BE follows with title generation, so
  // any other outcome settles the sidebar item on its placeholder title
  // immediately instead of leaving it waiting. A later successful turn on the
  // same chat still generates one (the BE only skips chats that already have a
  // title), and the item picks it up when it lands.
  //
  // A successful turn still isn't a guarantee: the title streams as a separate,
  // later TITLE frame with no delivery guarantee, so start the give-up timer
  // above too — if the title (or a fresh refetch carrying it) hasn't landed by
  // the time it fires, fall back to the same placeholder. Harmless if the title
  // already arrived: ChatHistoryItem only reacts to `titleUnavailable` while
  // still on the loading phase.
  const handleTurnSettled = (status: AiMessageStatusEnum | null) => {
    if (!selectedConversationId) return;
    if (status !== AiMessageStatusEnum.Complete) {
      setUntitledConversationId(selectedConversationId);
      return;
    }
    const conversationId = selectedConversationId;
    clearTimeout(titleWaitTimerRef.current);
    titleWaitTimerRef.current = setTimeout(() => {
      setUntitledConversationId((current) => current ?? conversationId);
    }, TITLE_WAIT_TIMEOUT_MS);
  };

  const renameTarget =
    conversations.find(({ id }) => id === renameTargetId) ||
    (createdConversation?.id === renameTargetId ? createdConversation : null);

  const handleSubmitRename = (title: string) => {
    if (!renameTargetId) return;
    renameConversation(renameTargetId, title)
      .then(() => {
        setRenameTargetId(null);
        toast({ content: t('ai-chat-renamed') });
      })
      .catch(() => undefined);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        caption={texts.modalTitle}
        mode={ModalMode.FullScreen}
        modalBodyClassName={styles.modalBody}
        modalContentClassName={styles.modalContent}
      >
        <div className={styles.layout}>
          <ChatSidebar
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            newConversationId={createdConversation?.id ?? null}
            untitledConversationId={untitledConversationId}
            search={search}
            onSearchChange={setSearch}
            onSelect={handleSelectConversation}
            onRename={setRenameTargetId}
            onDestroy={destroyConversation}
            destroyingId={destroyingId}
          />
          <ConversationPane
            key={paneKey}
            conversationId={selectedConversationId}
            busy={!!_get(selectedConversation, 'busy')}
            closed={
              _get(selectedConversation, 'status') ===
              AiConversationStatusEnum.Closed
            }
            user={user}
            feature={feature}
            texts={texts}
            onConversationCreated={handleConversationCreated}
            onConversationsChanged={refetch}
            onTurnSettled={handleTurnSettled}
            onStartNewChat={() => handleSelectConversation(null)}
            onUpgradePlan={() => setIsPlanModalOpen(true)}
            initialDocs={pendingDocs}
          />
        </div>
      </Modal>
      <UpgradePlanModal
        companyId={_get(user, 'company.id', '') as string}
        contactEmail={_get(user, 'email', '') as string}
        isOpen={isPlanModalOpen}
        planState={PlanState.Locked}
        preselectedFeatures={[CompanyPlanUpgradeFeatureEnum.AiAssistance]}
        getUserAccessByCompany={getUserAccessByCompany}
        usersLimit={_get(user, 'company.plan.usersLimit') as number | null}
        onClose={() => setIsPlanModalOpen(false)}
      />
      <AiRenameChatModal
        isOpen={!!renameTargetId}
        formKey={renameTargetId ?? 'none'}
        currentTitle={renameTarget?.title}
        loading={renameLoading}
        graphQLErrors={getGraphQLErrors(renameError)}
        onClose={() => setRenameTargetId(null)}
        onSubmit={handleSubmitRename}
      />
    </>
  );
}

export default AiAssistantModal;
